import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  feeDragExplanationBullets,
  feeDragHeadline,
  likelihoodExplanationBullets,
  prizeHeadline,
} from "@aucash/rules/lost-super";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, Segmented, Stepper } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// lost-super — lost / ATO-held super finder + multiple-account fee-drag
// explainer (the $18.9bn hook), surfaced stateless-v1 (PLAN arch decision #1).
// One screen, two modes over the shared lost-super ruleset; the result renders
// INLINE (no save-by-id, no auth gate, no persistence). Each compute is a
// stateless `calc.lostSuper.compute` query — input -> output, no round-trip to
// a stored row. Copy comes verbatim from the rules layer's copy helpers so the
// wording can never drift from the API/SEO surfaces (validation §3: information,
// not advice).
//
// HARD RULE (panel C2, binding): superannuation is an s764A financial product.
// This screen NEVER names, ranks, links, or implies a destination fund — every
// CTA routes ONLY to the ATO / myGov free search. Affiliate revenue is $0 by
// design. The rules-layer `assertNoFundHandoff` guard already strips any such
// term from the output we render, so the kill-line holds at the UI too.

const ASSESSMENT_YEAR = "2024-25";
const MYGOV_SEARCH_URL = "https://my.gov.au";
const ATO_KEEP_TRACK_URL =
  "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/keeping-track-of-your-super";

type Mode = "likelihood" | "fee-drag";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "likelihood", label: "Lost super check" },
  { value: "fee-drag", label: "Duplicate accounts" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  likelihood: {
    eyebrow: "ATO · lost & unclaimed super",
    title: "Could the ATO be holding super for you?",
    blurb:
      "Answer a few questions about your work and address history to see a general likelihood of having lost or ATO-held super. This is a literacy check, not a finding about your accounts — only the ATO's record can confirm what you have. The search is free.",
  },
  "fee-drag": {
    eyebrow: "Super · duplicate accounts",
    title: "What do duplicate super accounts cost?",
    blurb:
      "Holding more than one super account usually means paying admin fees and default insurance premiums twice. See an illustration of the recurring duplicate cost, on clearly disclosed assumptions.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.lostSuper.compute.query>
>;

export default function LostSuperIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("likelihood");

  // likelihood inputs (coarse, non-identifying — no TFN, no account numbers).
  const [employerCount, setEmployerCount] = useState(3);
  const [addressChanges, setAddressChanges] = useState(2);
  const [nameChanged, setNameChanged] = useState(false);
  const [hasDormantAccount, setHasDormantAccount] = useState(false);
  const [workedOverseas, setWorkedOverseas] = useState(false);
  const [hasConsolidatedBefore, setHasConsolidatedBefore] = useState(false);

  // fee-drag inputs.
  const [accountCount, setAccountCount] = useState(2);
  const [yearsToRetirement, setYearsToRetirement] = useState(25);
  const [duplicateInsurance, setDuplicateInsurance] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.lostSuper.compute.query>[0]["input"];
  } => {
    if (mode === "fee-drag") {
      return {
        input: {
          mode: "fee-drag",
          accountCount,
          yearsToRetirement,
          duplicateInsurance,
          assessmentYear: ASSESSMENT_YEAR,
        },
      };
    }
    return {
      input: {
        mode: "likelihood",
        employerCount,
        addressChanges,
        nameChanged,
        hasDormantAccount,
        workedOverseas,
        hasConsolidatedBefore,
        assessmentYear: ASSESSMENT_YEAR,
      },
    };
  };

  const onSubmit = (): void => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.lostSuper.compute
      .query(buildInput())
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Lost super" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "fee-drag" ? (
            <>
              <Field
                label="How many super accounts do you have?"
                help="Only the accounts beyond your main one are counted as duplicates."
              >
                <Stepper
                  value={accountCount}
                  onChange={setAccountCount}
                  min={1}
                  max={10}
                  suffix={accountCount === 1 ? "account" : "accounts"}
                  testID="account-count"
                  accessibilityLabel="Number of super accounts"
                />
              </Field>
              <Field
                label="Years until you expect to retire"
                help="The horizon the illustration compounds the duplicate cost over."
              >
                <Stepper
                  value={yearsToRetirement}
                  onChange={setYearsToRetirement}
                  min={0}
                  max={50}
                  suffix="years"
                  testID="years-to-retirement"
                  accessibilityLabel="Years until retirement"
                />
              </Field>
              <Field label="Do the duplicate accounts carry default insurance?">
                <Checkbox
                  checked={duplicateInsurance}
                  onChange={setDuplicateInsurance}
                  label="Yes — the extra accounts have default insurance I'm paying for."
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="How many employers have you had?"
                help="Each new job can open a default super account if you don't nominate one."
              >
                <Stepper
                  value={employerCount}
                  onChange={setEmployerCount}
                  min={0}
                  max={30}
                  suffix={employerCount === 1 ? "employer" : "employers"}
                  testID="employer-count"
                  accessibilityLabel="Number of employers"
                />
              </Field>
              <Field
                label="How many times have you moved home since starting work?"
                help="Lost mail from an old address can let a super account go inactive."
              >
                <Stepper
                  value={addressChanges}
                  onChange={setAddressChanges}
                  min={0}
                  max={30}
                  suffix="moves"
                  testID="address-changes"
                  accessibilityLabel="Number of address changes"
                />
              </Field>
              <Field label="Have you changed your legal name (e.g. marriage)?">
                <Checkbox
                  checked={nameChanged}
                  onChange={setNameChanged}
                  label="Yes — my legal name has changed since I started working."
                />
              </Field>
              <Field label="Do you know of any dormant or inactive super account?">
                <Checkbox
                  checked={hasDormantAccount}
                  onChange={setHasDormantAccount}
                  label="Yes — I've let a super account go inactive at some point."
                />
              </Field>
              <Field label="Have you ever worked overseas or as a non-resident?">
                <Checkbox
                  checked={workedOverseas}
                  onChange={setWorkedOverseas}
                  label="Yes — I've had a period of overseas or non-resident work."
                />
              </Field>
              <Field label="Have you ever checked or consolidated your super on myGov?">
                <Checkbox
                  checked={hasConsolidatedBefore}
                  onChange={setHasConsolidatedBefore}
                  label="Yes — I've checked or consolidated my super through myGov before."
                />
              </Field>
            </>
          )}

          <Button
            label={submitting ? "Checking…" : "See your result →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="lost-super-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

// The full information-not-advice disclaimer for the super surface. lost-super
// isn't an au08/au06/au13 product, and superannuation carries the §C2 fund-
// handoff posture, so it gets its own block (verbatim with the marketing
// surface). It renders at the TOP of the result, before any number (§3.2). NO
// destination-fund handoff — every pointer is to the free ATO / myGov process.
const SUPER_DISCLAIMER =
  "Educational tool. Not financial advice. This tool provides educational " +
  "information about lost and ATO-held super and the cost of holding multiple " +
  "super accounts, using the Australian Taxation Office's published figures. " +
  "The likelihood shown is a general, typical-case guide based on the history " +
  "you described — not a finding about your specific accounts. The duplicate-" +
  "cost figure is an illustration on disclosed assumptions, not a prediction. " +
  "We do not recommend or compare super funds. Only the ATO's record (via " +
  "myGov) can confirm what super you have. Before consolidating, check the " +
  "insurance and any exit fees on each account. Always verify at ato.gov.au.";

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer sits at the TOP, before any number (validation §3.2
 * placement rule). Copy is from the rules-layer helpers. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const bullets =
    result.mode === "likelihood"
      ? likelihoodExplanationBullets(
          result.output as Parameters<typeof prizeHeadline>[0],
          result.ruleVersion,
          result.sourcesVerifiedAt,
        )
      : feeDragExplanationBullets(
          result.output as Parameters<typeof feeDragHeadline>[0],
          result.ruleVersion,
          result.sourcesVerifiedAt,
        );

  const headline = headlineFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{SUPER_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your result · ATO figures</Eyebrow>
        <Chip label={result.mode === "fee-drag" ? "Illustration" : "Guide"} tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>

      {/* The ONLY handoff is to the free government process — never a fund
          (panel C2). */}
      <Section title="The free next step (ATO / myGov)">
        <Text style={styles.bodyText}>
          Checking and consolidating your super is free and done through the
          government's own services — there's nothing to buy and no fund to join
          here.
        </Text>
        <SourceLink label="Sign in to myGov" url={MYGOV_SEARCH_URL} />
        <SourceLink label="ATO — keeping track of your super" url={ATO_KEEP_TRACK_URL} />
      </Section>

      <Section title="Where these numbers come from">
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Figures last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** The hero line per mode — from the rules-layer copy helpers (no inline
 * figures, no advice framing, no fund handoff). */
function headlineFor(result: ComputeResult): string {
  if (result.mode === "fee-drag") {
    return feeDragHeadline(result.output as Parameters<typeof feeDragHeadline>[0]);
  }
  return prizeHeadline(result.output as Parameters<typeof prizeHeadline>[0]);
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
